import axios from 'axios';
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';
import Breadcrumb from '../../../components/common/breadcrumb';
import api from '../../../services/api-configuration-v3';
// Import components
import FileUploadArea from './components/FileUploadArea';
import GenerateReportButton from './components/GenerateReportButton';
import ReportTypeSelector from './components/ReportTypeSelector';

// Import utilities and constants
import { REPORT_OPTIONS } from './constants/reportOptions';
import { createTemplateExcel, downloadFile, readExcelFile, validateExcelFile, validateExcelHeader } from './utils/fileUtils';

// Main Component

const ExceldenRaporla = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);
    const [saseList, setSaseList] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [cancelToken, setCancelToken] = useState(null);

    // Toast notification functions
    const notifySuccess = useCallback((message) => {
        toast.success(message, { autoClose: 3000 });
    }, []);

    const notifyError = useCallback((message) => {
        toast.error(message, { autoClose: 5000 });
    }, []);

    // Excel file processing
    const processExcelFile = useCallback(async (file) => {
        setIsProcessing(true);
        try {
            const saseNumbers = await readExcelFile(file);
            setSelectedFile(file);
            setSaseList(saseNumbers);
            return { success: true, count: saseNumbers.length };
        } catch (error) {
            setSelectedFile(null);
            setSaseList([]);
            return { success: false, error: error.message };
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const clearFile = useCallback(() => {
        setSelectedFile(null);
        setSaseList([]);

        // File input'u da temizle
        const fileInput = document.getElementById('fileInput');
        if (fileInput) {
            fileInput.value = '';
        }
    }, []);

    // Report generation
    const generateReport = useCallback(async (reportId, file) => {
        setIsGenerating(true);

        // Yeni CancelToken oluştur
        const source = axios.CancelToken.source();
        setCancelToken(source);

        try {
            const formData = new FormData();
            formData.append('report_id', reportId);
            formData.append('file', file);

            // Seçili raporun columnOrder bilgisini al
            const selectedReportInfo = REPORT_OPTIONS.find(opt => opt.id === reportId);
            if (selectedReportInfo && selectedReportInfo.columnOrder) {
                formData.append('columnOrder', JSON.stringify(selectedReportInfo.columnOrder));
            }

            const response = await api.post('/reports/getReportByChassisNo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                responseType: 'blob',
                cancelToken: source.token,
                timeout: 0 // Timeout'u kaldır
            });

            return { success: true, data: response.data };
        } catch (error) {
            if (axios.isCancel(error)) {
                return { success: false, error: 'Rapor oluşturma iptal edildi' };
            }


            // Empty data durumunu kontrol et
            if (error?.message?.includes('empty data')) {
                return { success: false, error: 'Veri bulunamadı', isWarning: true };
            }

            return {
                success: false,
                error: error?.message ? 'HATA: ' + error?.message : 'Bağlantı hatası'
            };
        } finally {
            setIsGenerating(false);
            setCancelToken(null);
            console.log('CancelToken temizlendi');
        }
    }, []);

    // File change handler
    const handleFileChange = useCallback(async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const headerValidation = await validateExcelHeader(file);
        if (!headerValidation.isValid) {
            notifyError(headerValidation.error);
            clearFile();
            return;
        }

        const validation = validateExcelFile(file);
        if (!validation.isValid) {
            notifyError(validation.error);
            clearFile();
            return;
        }

        const result = await processExcelFile(file);

        if (result.success) {
            if (result.count === 0) {
                notifyError('Excel dosyasının A kolonunda şase numarası bulunamadı');
                clearFile();
            } else {
                //  notifySuccess(`${result.count} şase numarası yüklendi`);
            }
        } else {
            notifyError(result.error);
        }
    }, [processExcelFile, notifySuccess, notifyError, clearFile]);

    // Report change handler
    const handleReportChange = useCallback((e) => {
        setSelectedReport(e.value);
    }, []);

    // File area click handler
    const handleFileAreaClick = useCallback(() => {
        if (!isProcessing) {
            // Önce mevcut dosya seçimini temizle
            clearFile();
            // Sonra dosya seçim dialogunu aç
            document.getElementById('fileInput').click();
        }
    }, [isProcessing, clearFile]);

    // Generate report handler
    const handleGenerateReport = useCallback(async () => {
        if (!selectedFile) {
            notifyError('Lütfen bir Excel dosyası seçiniz');
            return;
        }

        if (!selectedReport) {
            notifyError('Lütfen bir rapor türü seçiniz');
            return;
        }

        const result = await generateReport(selectedReport, selectedFile);

        if (result.success) {
            const blob = new Blob([result.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const reportName = REPORT_OPTIONS.find(opt => opt.id === selectedReport)?.name || 'rapor';
            const fileName = `${reportName}-${new Date().toISOString().split('T')[0]}.xlsx`;

            downloadFile(blob, fileName);
            notifySuccess('Rapor başarıyla indirildi');
        } else {
            // İptal edildiğinde veya veri bulunamadığında warning toast göster
            if (result.error === 'Rapor oluşturma iptal edildi' || result.isWarning) {
                toast.warning(result.error, { autoClose: 3000 });
            } else {
                notifyError(result.error);
            }
        }
    }, [selectedFile, selectedReport, generateReport, notifySuccess, notifyError]);

    // Template download handler
    const handleTemplateDownload = useCallback(() => {
        const templateDownload = document.getElementById('templateDownload');
        templateDownload.disabled = true;
        try {
            const templateBlob = createTemplateExcel();
            const fileName = `ExceldenRaporla-${new Date().toISOString().split('T')[0]}.xlsx`;
            downloadFile(templateBlob, fileName);
            //  notifySuccess('Şablon başarıyla indirildi');
        } catch (error) {
            notifyError('Şablon oluşturulurken hata oluştu');
        } finally {
            templateDownload.disabled = false;
        }
    }, [notifySuccess, notifyError]);

    // Cancel report generation
    const handleCancelReport = useCallback(() => {

        if (cancelToken) {
            cancelToken.cancel('Rapor oluşturma iptal edildi');

            // İptal edildiğinde de dosya seçimini temizle
            clearFile();
        } else {
            console.log('CancelToken bulunamadı');
        }
    }, [cancelToken, clearFile]);

    // Seçili raporun aktif olup olmadığını kontrol et
    const isSelectedReportActive = useMemo(() => {
        if (selectedReport === null || selectedReport === undefined) return false;
        if (selectedReport === 0) return false;
        const reportInfo = REPORT_OPTIONS.find(opt => opt.id === selectedReport);
        return reportInfo && reportInfo.id !== null && reportInfo.id !== 0;
    }, [selectedReport]);

    // Memoized disabled state
    const isGenerateDisabled = useMemo(() => {
        return isGenerating || !selectedFile || !selectedReport || saseList.length === 0 || !isSelectedReportActive;
    }, [isGenerating, selectedFile, selectedReport, saseList.length, isSelectedReportActive]);

    return (
        <div>
            <Breadcrumb
                parent="Raporlar"
                current="Excelden Raporlama"
            />
            <div className="container-fluid">
                <Row className="justify-content-center">
                    <Col lg="12">
                        <Card className="border-0 shadow-sm">
                            <CardBody className="p-3">
                                <Row>
                                    <Col md="12" className='d-flex justify-content-end gap-2'>
                                        {isGenerating && (
                                            <Button
                                                color="danger"
                                                className="mb-3 mr-2"
                                                size="sm"
                                                onClick={handleCancelReport}
                                            >
                                                <i className="fa fa-times mr-2"></i>
                                                İptal Et
                                            </Button>
                                        )}
                                        <Button
                                            color="warning"
                                            className="mb-3"
                                            size="sm"
                                            id='templateDownload'
                                            onClick={handleTemplateDownload}
                                        >
                                            <i className="fa fa-download mr-2"></i>
                                            Şablon İndir
                                        </Button>
                                    </Col>
                                </Row>

                                <Row className="g-3 align-items-end">
                                    <Col md="4">
                                        <FileUploadArea
                                            selectedFile={selectedFile}
                                            saseCount={saseList.length}
                                            isProcessing={isProcessing}
                                            isGenerating={isGenerating}
                                            onFileSelect={handleFileChange}
                                            onAreaClick={handleFileAreaClick}
                                        />
                                    </Col>

                                    <Col md="4">
                                        <ReportTypeSelector
                                            reportOptions={REPORT_OPTIONS}
                                            selectedReport={selectedReport}
                                            isGenerating={isGenerating}
                                            onReportChange={handleReportChange}
                                        />
                                    </Col>

                                    <Col md="4">
                                        <GenerateReportButton
                                            isLoading={isGenerating}
                                            isDisabled={isGenerateDisabled}
                                            onClick={handleGenerateReport}
                                        />
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default ExceldenRaporla;
